import { supabase } from '../lib/supabase';

export interface LoanCalculation {
  principal: number;
  interestRate: number;
  durationMonths: number;
  monthlyPayment: number;
  totalRepayment: number;
  totalInterest: number;
  schedule: Array<{
    month: number;
    payment: number;
    principal: number;
    interest: number;
    balance: number;
  }>;
}

export interface LoanRequestRow {
  id: string;
  group_id: string;
  member_id: string;
  requested_amount: number;
  purpose: string;
  purpose_notes?: string | null;
  repayment_months: number;
  interest_rate: number;
  total_repayment_amount: number;
  monthly_payment_amount: number;
  member_total_contributions?: number;
  loan_type?: string;
  status: string;
  admin_notes?: string | null;
  requested_by?: string | null;
  created_at: string;
  member?: { id: string; full_name?: string; email?: string; phone?: string };
  requester?: { id: string; full_name?: string; email?: string };
}

export interface ActiveLoanRow {
  id: string;
  group_id: string;
  member_id: string;
  principal_amount: number;
  total_amount: number;
  remaining_balance: number;
  monthly_payment: number;
  repayment_months: number;
  months_paid: number;
  interest_rate: number;
  status: string;
  next_payment_date?: string;
  created_at: string;
  member?: { id: string; full_name?: string; email?: string };
  loan_request?: { purpose?: string; purpose_notes?: string };
}

export interface LoanPaymentRow {
  id: string;
  loan_id: string;
  amount_paid: number;
  payment_date: string;
  recorded_by?: string | null;
  payment_notes?: string | null;
  interest_portion?: number | null;
}

export interface LoanSettings {
  group_id?: string;
  interest_rate?: number;
  min_contribution_amount?: number;
  max_loan_ratio?: number;
  max_repayment_months?: number;
  default_loan_type?: string;
}

class LoanService {
  /**
   * Group cash in hand (total approved savings minus outstanding group-type loan balances).
   * Backed by Supabase RPC: get_group_contribution_available_for_loans(target_group_id uuid)
   */
  async getGroupCashInHand(groupId: string): Promise<number> {
    try {
      const { data, error } = await supabase.rpc('get_group_contribution_available_for_loans', {
        target_group_id: groupId,
      });
      if (error) throw error;
      return Number(data || 0);
    } catch (e) {
      console.error('Error fetching group cash in hand:', e);
      return 0;
    }
  }

  /**
   * Calculate loan details
   */
  calculateLoan(
    principal: number,
    annualInterestRate: number,
    durationMonths: number
  ): LoanCalculation {
    const monthlyRate = annualInterestRate / 100 / 12;

    // Calculate monthly payment using amortization formula
    const monthlyPayment = monthlyRate === 0
      ? principal / durationMonths
      : (principal * monthlyRate * Math.pow(1 + monthlyRate, durationMonths)) /
      (Math.pow(1 + monthlyRate, durationMonths) - 1);

    const totalRepayment = monthlyPayment * durationMonths;
    const totalInterest = totalRepayment - principal;

    // Generate payment schedule
    const schedule: LoanCalculation['schedule'] = [];
    let balance = principal;

    for (let month = 1; month <= durationMonths; month++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = monthlyPayment - interestPayment;
      balance -= principalPayment;

      schedule.push({
        month,
        payment: monthlyPayment,
        principal: principalPayment,
        interest: interestPayment,
        balance: Math.max(0, balance),
      });
    }

    return {
      principal,
      interestRate: annualInterestRate,
      durationMonths,
      monthlyPayment,
      totalRepayment,
      totalInterest,
      schedule,
    };
  }

  async getMemberTotalContributions(memberId: string, groupId: string): Promise<number> {
    const { data, error } = await supabase.rpc('get_member_total_contributions', {
      target_member_id: memberId,
      target_group_id: groupId,
    });
    if (error) throw error;
    return Number(data || 0);
  }

  async submitLoanRequest(params: {
    groupId: string;
    memberId: string;
    requestedAmount: number;
    purpose: string;
    purposeNotes?: string;
    repaymentMonths: number;
    interestRate: number;
    totalRepaymentAmount: number;
    monthlyPaymentAmount: number;
    memberTotalContributions: number;
    loanType?: string;
    requestedBy?: string;
  }): Promise<{ success: boolean; requestId?: string; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('loan_requests')
        .insert({
          group_id: params.groupId,
          member_id: params.memberId,
          requested_amount: params.requestedAmount,
          purpose: params.purpose,
          purpose_notes: params.purposeNotes?.trim() || null,
          repayment_months: params.repaymentMonths,
          interest_rate: params.interestRate,
          total_repayment_amount: params.totalRepaymentAmount,
          monthly_payment_amount: params.monthlyPaymentAmount,
          member_total_contributions: params.memberTotalContributions,
          loan_type: params.loanType || 'group_total_contribution',
          status: 'pending',
          requested_by: params.requestedBy || null,
        })
        .select('id')
        .single();

      if (error) throw error;
      return { success: true, requestId: data.id };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  async getLoanRequests(groupId: string, status?: string): Promise<LoanRequestRow[]> {
    let query = supabase
      .from('loan_requests')
      .select(`
        *,
        member:member_id(id, full_name, email, phone),
        requester:requested_by(id, full_name, email)
      `)
      .eq('group_id', groupId);

    if (status) query = query.eq('status', status);

    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as LoanRequestRow[];
  }

  async getMemberLoanRequests(memberId: string): Promise<LoanRequestRow[]> {
    const { data, error } = await supabase
      .from('loan_requests')
      .select(`
        *,
        group:group_id(id, name, currency),
        member:member_id(id, full_name, email, phone),
        requester:requested_by(id, full_name, email)
      `)
      .eq('member_id', memberId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as LoanRequestRow[];
  }

  async getActiveLoans(groupId: string): Promise<ActiveLoanRow[]> {
    const { data, error } = await supabase
      .from('loans')
      .select(`
        *,
        member:member_id(id, full_name, email),
        loan_request:loan_request_id(purpose, purpose_notes)
      `)
      .eq('group_id', groupId)
      .in('status', ['active', 'defaulted'])
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as ActiveLoanRow[];
  }

  async getAllLoans(groupId: string): Promise<ActiveLoanRow[]> {
    const { data, error } = await supabase
      .from('loans')
      .select(`
        *,
        member:member_id(id, full_name, email),
        loan_request:loan_request_id(purpose, purpose_notes)
      `)
      .eq('group_id', groupId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as ActiveLoanRow[];
  }

  async getMemberLoans(memberId: string): Promise<ActiveLoanRow[]> {
    const { data, error } = await supabase
      .from('loans')
      .select(`
        *,
        group:group_id(id, name, currency),
        loan_request:loan_request_id(purpose, purpose_notes)
      `)
      .eq('member_id', memberId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as ActiveLoanRow[];
  }

  async approveLoanRequest(
    requestId: string,
    adminUserId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('approve_loan_request', {
        request_id: requestId,
        admin_user_id: adminUserId,
      });
      if (error) throw error;
      const result = data as { success?: boolean; error?: string };
      if (result?.success === false) {
        return { success: false, error: result.error || 'Approval failed' };
      }
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  async rejectLoanRequest(
    requestId: string,
    adminUserId: string,
    rejectionNotes: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.rpc('reject_loan_request', {
        request_id: requestId,
        admin_user_id: adminUserId,
        rejection_notes: rejectionNotes,
      });
      if (error) throw error;
      const result = data as { success?: boolean; error?: string };
      if (result?.success === false) {
        return { success: false, error: result.error || 'Rejection failed' };
      }
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  async getLoanPayments(loanId: string): Promise<LoanPaymentRow[]> {
    const { data, error } = await supabase
      .from('loan_payments')
      .select('id, loan_id, amount_paid, payment_date, recorded_by, payment_notes, interest_portion')
      .eq('loan_id', loanId)
      .order('payment_date', { ascending: false });

    if (error) throw error;
    return (data || []) as LoanPaymentRow[];
  }

  async recordLoanPayment(params: {
    loanId: string;
    amountPaid: number;
    recordedBy: string;
    paymentNotes?: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: loan, error: loanError } = await supabase
        .from('loans')
        .select('id, total_amount, remaining_balance, months_paid, status')
        .eq('id', params.loanId)
        .maybeSingle();

      if (loanError || !loan) {
        return { success: false, error: 'Loan not found' };
      }

      const totalAmount = Number(loan.total_amount || 0);
      const existingPayments = await this.getLoanPayments(params.loanId);
      const totalPaidSoFar = existingPayments.reduce(
        (sum, p) => sum + Number(p.amount_paid || 0),
        0
      );
      const actualRemaining = totalAmount - totalPaidSoFar;

      if (actualRemaining <= 0.01) {
        return { success: false, error: 'This loan is already fully paid' };
      }
      if (params.amountPaid > actualRemaining + 0.01) {
        return {
          success: false,
          error: `Payment cannot exceed remaining balance of ${actualRemaining.toFixed(2)}`,
        };
      }

      let interestPortion = 0;
      try {
        const { data: interestResult } = await supabase.rpc('calculate_payment_interest_portion', {
          p_loan_id: params.loanId,
          p_amount_paid: params.amountPaid,
        });
        interestPortion = Number(interestResult || 0);
      } catch {
        interestPortion = 0;
      }

      const insertPayload: Record<string, unknown> = {
        loan_id: params.loanId,
        amount_paid: params.amountPaid,
        recorded_by: params.recordedBy,
        payment_notes: params.paymentNotes?.trim() || null,
      };

      let insertError = null;
      const firstTry = await supabase.from('loan_payments').insert({
        ...insertPayload,
        interest_portion: interestPortion,
      });
      insertError = firstTry.error;

      if (insertError) {
        const retry = await supabase.from('loan_payments').insert(insertPayload);
        if (retry.error) throw retry.error;
      }

      const newTotalPaid = totalPaidSoFar + params.amountPaid;
      const newBalance = Math.max(0, totalAmount - newTotalPaid);
      const newStatus = newBalance <= 0.01 ? 'completed' : 'active';
      const nextDate = new Date();
      if (newStatus !== 'completed') {
        nextDate.setDate(nextDate.getDate() + 30);
      }
      const nextPaymentDate = nextDate.toISOString().split('T')[0];

      await supabase
        .from('loans')
        .update({
          remaining_balance: newBalance,
          months_paid: Number(loan.months_paid || 0) + 1,
          status: newStatus,
          next_payment_date: nextPaymentDate,
        })
        .eq('id', params.loanId);

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /** @deprecated Use recordLoanPayment for group-admin flows */
  async recordPayment(
    loanId: string,
    amount: number,
    _paymentMethod: string,
    reference: string
  ): Promise<{ success: boolean; error?: string }> {
    const { data: auth } = await supabase.auth.getUser();
    return this.recordLoanPayment({
      loanId,
      amountPaid: amount,
      recordedBy: auth.user?.id || '',
      paymentNotes: reference,
    });
  }

  async getLoanSettings(groupId: string): Promise<LoanSettings> {
    const { data } = await supabase
      .from('loan_settings')
      .select('*')
      .eq('group_id', groupId)
      .maybeSingle();

    return data || {
      interest_rate: 10,
      min_contribution_amount: 0,
      max_repayment_months: 12,
      default_loan_type: 'group_total_contribution',
    };
  }

  /**
   * Update loan settings
   */
  async updateLoanSettings(
    groupId: string,
    settings: Partial<LoanSettings>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('loan_settings')
        .upsert({ group_id: groupId, ...settings });

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('Error updating loan settings:', error);
      return { success: false, error: error.message };
    }
  }

}

export const loanService = new LoanService();
